import { useState, useRef, useEffect, useCallback } from "react";
import type { RuleGroupType } from "react-querybuilder";
import { apiGet } from "../../api";
import {
  type StoredFilter,
  getDefaultFilter,
  setDefaultFilter,
  clearDefaultFilter,
  setActiveFilter,
  clearActiveFilter,
} from "./filterStorage";

interface SavedFilter {
  id: number;
  name: string;
  rules: RuleGroupType;
}

interface Props {
  contentType: number;
  defaultQuery: RuleGroupType;
  activeFilter: string | null;
  onActiveFilterChange: (name: string | null) => void;
  onSelect: (query: RuleGroupType, label: string) => void;
}

const SaveIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M16 21V19C16 17.114 16 16.172 15.414 15.586C14.828 15 13.886 15 12 15H11C9.114 15 8.172 15 7.586 15.586C7 16.172 7 17.114 7 19V21" stroke="currentColor" />
    <path d="M7 8H12" stroke="currentColor" strokeLinecap="round" />
    <path d="M3 9C3 6.172 3 4.757 3.879 3.879C4.757 3 6.172 3 9 3H16.172C16.58 3 16.785 3 16.968 3.076C17.151 3.152 17.297 3.296 17.586 3.586L20.414 6.414C20.704 6.704 20.848 6.848 20.924 7.032C21 7.215 21 7.42 21 7.828V15C21 17.828 21 19.243 20.121 20.121C19.243 21 17.828 21 15 21H9C6.172 21 4.757 21 3.879 20.121C3 19.243 3 17.828 3 15V9Z" stroke="currentColor" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export default function FilterSelector({ contentType, defaultQuery, activeFilter, onActiveFilterChange, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<SavedFilter[]>([]);
  const [defaultFilterId, setDefaultFilterId] = useState<number | null>(
    () => getDefaultFilter(contentType)?.id ?? null,
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || contentType === 0) return;
    apiGet("/api/configuration/query_history/", { content_type_id: contentType }).then((data: any) => {
      setFilters(data);
    });
  }, [open, contentType]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const handleSelect = (filter: SavedFilter) => {
    setOpen(false);
    onActiveFilterChange(filter.name);
    setActiveFilter(contentType, filter as StoredFilter);
    onSelect(filter.rules, filter.name);
  };

  const handleClear = () => {
    onActiveFilterChange(null);
    clearActiveFilter(contentType);
    onSelect(defaultQuery, "");
  };

  const handleToggleDefault = useCallback((e: React.MouseEvent, filter: SavedFilter) => {
    e.stopPropagation();
    const isDefault = defaultFilterId === filter.id;
    if (isDefault) {
      setDefaultFilterId(null);
      clearDefaultFilter(contentType);
    } else {
      setDefaultFilterId(filter.id);
      setDefaultFilter(contentType, filter as StoredFilter);
    }
  }, [contentType, defaultFilterId]);

  return (
    <div className="qf-filter-selector" ref={ref}>
      <button
        type="button"
        className="qf-filter-selector__toggle"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="qf-filter-selector__icon"><SaveIcon /></span>
        <span className="qf-filter-selector__label">Избранные фильтры</span>
        <span className={`qf-filter-selector__chevron${open ? " qf-filter-selector__chevron--open" : ""}`}>
          <ChevronIcon />
        </span>
      </button>

      {activeFilter && (
        <>
          <span className="qf-filter-selector__divider" />
          <span className="qf-filter-selector__active">{activeFilter}</span>
          <button type="button" className="qf-filter-selector__clear" onClick={handleClear}>
            <CloseIcon />
          </button>
        </>
      )}

      {open && (
        <div className="qf-filter-selector__menu">
          {filters.map((f) => (
            <div
              key={f.id}
              className={`qf-filter-selector__item${activeFilter === f.name ? " qf-filter-selector__item--active" : ""}`}
              onClick={() => handleSelect(f)}
            >
              <button
                type="button"
                className={`qf-filter-selector__star${defaultFilterId === f.id ? " qf-filter-selector__star--filled" : ""}`}
                onClick={(e) => handleToggleDefault(e, f)}
                title={defaultFilterId === f.id ? "Убрать из фильтра по умолчанию" : "Сделать фильтром по умолчанию"}
              >
                <StarIcon filled={defaultFilterId === f.id} />
              </button>
              <span className="qf-filter-selector__item-name" title={f.name}>{f.name}</span>
            </div>
          ))}
          {filters.length === 0 && (
            <div className="qf-filter-selector__empty">Нет избранных фильтров</div>
          )}
        </div>
      )}
    </div>
  );
}
